import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router'
import * as Yup from 'yup'

// @material-ui
import { Typography, Button, Grid } from '@material-ui/core'
import { withStyles } from '@material-ui/styles'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { withVisitorInfo } from '_core/utils/visitorInfo'
import { TextInput } from '_core/components/TextInput'
import { FormValidator } from '_core/components/FormValidator'

// style
import { verifyEmailStepFormStyle } from './verifyEmailStepFormStyle'

const RawVerifyEmailStepForm = (props) => {
  const { classes, update, isExpired, visitorInfo, getLangString: l } = props

  const {
    request: { queue },
    notify: { enqueueSnackbar },
    config: { API_METHOD, ROUTE_URL_PARAM, VERIFICATION_TYPE },
  } = useGlobal()

  const history = useHistory()
  const location = useLocation()

  const search = new URLSearchParams(location.search)
  const code = search.get(ROUTE_URL_PARAM.CODE)

  if (code) {
    search.delete(ROUTE_URL_PARAM.CODE)
    location.search = search.toString()
    history.push(location)
  }

  const [values, setValues] = useState({
    email: visitorInfo.info.email,
  })

  const getOnChange = field => value => {
    setValues(oldValues => ({
      ...oldValues,
      [field]: value,
    }))
  }

  const onSubmit = async event => {
    event.preventDefault()

    try {
      await queue(API_METHOD.VISITOR_VERIFICATION_START, {
        type: VERIFICATION_TYPE.EMAIL,
        ...values,
      })

      update()
    } catch (error) {
      process.env.NODE_ENV !== 'production' && console.error(error)
      enqueueSnackbar(error.message, { variant: 'error' })
    }
  }

  const fields = {
    email: {
      value: values['email'],
      label: l('r._.profile.verify.email.label'),
      onChange: getOnChange('email'),
      ban: /\s/g,
      match: /^[^@\s]+@([^@\s.]+\.)+[^@\s.]+$/,
      shape: Yup.string(l('r._.profile.verify.email.label'))
        .trim()
        .ensure()
        .required('empty')
        .email(),
    },
  }

  return (
    <form onSubmit={onSubmit} className={classes.root}>
      {isExpired ? (
        <>
          <Typography variant="body2">
            {l('r._.profile.verify.email.text.codeexpiredtitle')}
          </Typography>
          <Typography variant="caption" gutterBottom={true}>
            {l('r._.profile.verify.email.text.codeexpiredbody')}
          </Typography>
        </>
      ) : null}

      <FormValidator
        fields={fields}
        touchOnInit={true}
        render={({ form, canSubmit }) => (
          <Grid container={true}>
            <Grid item={true} xs={12} sm={8}>
              <TextInput {...form['email']} />
            </Grid>

            <Grid item={true} xs={12} sm={4}>
              <Button type="submit" fullWidth={true} disabled={!canSubmit}>
                {l('r._.profile.verify.control.submit')}
              </Button>
            </Grid>
          </Grid>
        )}
      />
    </form>
  )
};

RawVerifyEmailStepForm.defaultProps = {}

RawVerifyEmailStepForm.propTypes = {
  // self props
  isExpired: PropTypes.bool,
  update: PropTypes.func.isRequired,

  // withVisitorInfo
  visitorInfo: PropTypes.object.isRequired,

  // `withStyles` HOC props
  classes: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const VerifyEmailStepForm = withVisitorInfo(withLang(
  withStyles(verifyEmailStepFormStyle)(RawVerifyEmailStepForm)
))
